/**
 * Versioned fitness-estimate tables. Every cell has an origin:
 * published, interpolated (between published anchors), or estimated
 * (digitized / reconstructed — see docs/NORMS.md).
 */
export const NORMS_VERSION = '2026.4';

export type NormOrigin = 'published' | 'interpolated' | 'estimated';

export interface NormGroup {
  ageMin: number;
  ageMax: number;
  sex: 'male' | 'female';
  mean: number;
  /** Between-person SD when the paper reports one. */
  sd?: number;
  origin: NormOrigin;
  /** Placeholder pending source verification. */
  todo?: boolean;
}

export interface NormTable {
  id: string;
  citation: string;
  unit: 'reps' | 's' | 'm/s' | 'points';
  higherIsBetter: boolean;
  normsVersion: string;
  /** Fallback SD when a group omits one. */
  defaultSd: number;
  /** Typical measurement error in the same unit as `mean`. */
  measError: number;
  todo?: boolean;
  groups: NormGroup[];
}

export interface NormRow {
  ageMin: number;
  ageMax: number;
  male: number;
  female: number;
  maleSd?: number;
  femaleSd?: number;
  origin?: NormOrigin;
  todo?: boolean;
}

function byAge(
  citation: string,
  id: string,
  unit: NormTable['unit'],
  higherIsBetter: boolean,
  defaultSd: number,
  measError: number,
  rows: NormRow[]
): NormTable {
  const groups: NormGroup[] = [];
  for (const row of rows) {
    const origin = row.origin ?? 'published';
    groups.push({
      ageMin: row.ageMin,
      ageMax: row.ageMax,
      sex: 'male',
      mean: row.male,
      sd: row.maleSd,
      origin,
      todo: row.todo || undefined,
    });
    groups.push({
      ageMin: row.ageMin,
      ageMax: row.ageMax,
      sex: 'female',
      mean: row.female,
      sd: row.femaleSd,
      origin,
      todo: row.todo || undefined,
    });
  }
  return {
    id,
    citation,
    unit,
    higherIsBetter,
    normsVersion: NORMS_VERSION,
    defaultSd,
    measError,
    groups,
  };
}

/**
 * 30-second chair stand.
 * 18–59: Tveter 2014 means (Norwegian community sample).
 * 60–94: Rikli & Jones 1999 SFT means (community-residing US, n=7,183).
 * Pooled SD 3.4 from Jones, Rikli & Beam 1999 test–retest sample.
 */
export const CST30_NORMS: NormTable = byAge(
  'Rikli RE, Jones CJ. Functional fitness normative scores for community-residing older adults, ages 60-94. J Aging Phys Act. 1999;7:162-181. Tveter AT, Dagfinrud H, Moseng T, Holm I. Health-related physical fitness measures: reference values and reference equations. Arch Phys Med Rehabil. 2014;95:1366-1373.',
  'cst30',
  'reps',
  true,
  3.4,
  1,
  [
    { ageMin: 18, ageMax: 29, male: 27, female: 26, origin: 'published' },
    { ageMin: 30, ageMax: 39, male: 27, female: 24, origin: 'published' },
    { ageMin: 40, ageMax: 49, male: 29, female: 25, origin: 'published' },
    { ageMin: 50, ageMax: 59, male: 25, female: 24, origin: 'published' },
    { ageMin: 60, ageMax: 64, male: 14.2, female: 12.1, origin: 'published' },
    { ageMin: 65, ageMax: 69, male: 13.4, female: 11.9, origin: 'published' },
    { ageMin: 70, ageMax: 74, male: 12.6, female: 11.4, origin: 'published' },
    { ageMin: 75, ageMax: 79, male: 11.8, female: 10.8, origin: 'published' },
    { ageMin: 80, ageMax: 84, male: 10.6, female: 9.6, origin: 'published' },
    { ageMin: 85, ageMax: 89, male: 9.5, female: 8.7, origin: 'published' },
    { ageMin: 90, ageMax: 94, male: 8.0, female: 7.5, origin: 'published' },
  ]
);

/**
 * Strassmann 2013 1-min STS medians (p50), ages 20–79.
 */
export const CST60_NORMS: NormTable = byAge(
  'Strassmann A, Steurer-Stey C, Dalla Lana K, et al. Population-based reference values for the 1-min sit-to-stand test. Int J Public Health. 2013;58:949-953.',
  'cst60',
  'reps',
  true,
  8,
  2,
  [
    { ageMin: 20, ageMax: 24, male: 50, female: 47 },
    { ageMin: 25, ageMax: 29, male: 48, female: 47 },
    { ageMin: 30, ageMax: 34, male: 47, female: 45 },
    { ageMin: 35, ageMax: 39, male: 47, female: 42 },
    { ageMin: 40, ageMax: 44, male: 45, female: 41 },
    { ageMin: 45, ageMax: 49, male: 44, female: 41 },
    { ageMin: 50, ageMax: 54, male: 42, female: 39 },
    { ageMin: 55, ageMax: 59, male: 41, female: 36 },
    { ageMin: 60, ageMax: 64, male: 37, female: 34 },
    { ageMin: 65, ageMax: 69, male: 35, female: 33 },
    { ageMin: 70, ageMax: 74, male: 32, female: 30 },
    { ageMin: 75, ageMax: 79, male: 30, female: 27 },
  ]
);

/**
 * Springer 2007 unipedal stance, best of 3, 45 s cap.
 * Parenthetical values in the paper are treated as SD (see NORMS.md).
 */
export const SLS_EO_NORMS: NormTable = byAge(
  'Springer BA, Marin R, Cyhan T, Roberts H, Gill NW. Normative values for the unipedal stance test with eyes open and closed. J Geriatr Phys Ther. 2007;30(1):8-15.',
  'sls_eo',
  's',
  true,
  12,
  1,
  [
    { ageMin: 18, ageMax: 39, male: 44.4, female: 45.1, maleSd: 4.1, femaleSd: 0.1 },
    { ageMin: 40, ageMax: 49, male: 41.6, female: 42.1, maleSd: 10.2, femaleSd: 9.5 },
    { ageMin: 50, ageMax: 59, male: 41.5, female: 40.9, maleSd: 10.5, femaleSd: 10.0 },
    { ageMin: 60, ageMax: 69, male: 33.8, female: 30.4, maleSd: 16.0, femaleSd: 16.4 },
    { ageMin: 70, ageMax: 79, male: 25.9, female: 16.7, maleSd: 18.1, femaleSd: 15.0 },
    { ageMin: 80, ageMax: 99, male: 8.7, female: 10.6, maleSd: 12.6, femaleSd: 13.2 },
  ]
);

export const SLS_EC_NORMS: NormTable = byAge(
  'Springer BA, Marin R, Cyhan T, Roberts H, Gill NW. J Geriatr Phys Ther. 2007;30(1):8-15. Eyes-closed unipedal stance, best of 3.',
  'sls_ec',
  's',
  true,
  8,
  1,
  [
    { ageMin: 18, ageMax: 39, male: 16.9, female: 13.1, maleSd: 13.9, femaleSd: 12.3 },
    { ageMin: 40, ageMax: 49, male: 12.0, female: 13.5, maleSd: 13.5, femaleSd: 12.4 },
    { ageMin: 50, ageMax: 59, male: 8.6, female: 7.9, maleSd: 8.8, femaleSd: 8.0 },
    { ageMin: 60, ageMax: 69, male: 5.1, female: 3.6, maleSd: 6.8, femaleSd: 2.3 },
    { ageMin: 70, ageMax: 79, male: 2.6, female: 3.7, maleSd: 1.7, femaleSd: 6.2 },
    { ageMin: 80, ageMax: 99, male: 1.8, female: 2.1, maleSd: 0.9, femaleSd: 1.1 },
  ]
);

/**
 * STEADI 10-second single-leg hold. Means are Springer 2007 eyes-open
 * best-of-3 values capped at 10 s. Scoring uses SLS_EO_NORMS plus the
 * protocol cap in ageEquivalent — do not invert 10 s on this table.
 */
export const SLS_10_NORMS: NormTable = byAge(
  'CDC STEADI 4-stage balance protocol (10-second single-leg hold). Age curve from Springer 2007 eyes-open best-of-3, capped at 10 s.',
  'sls_10',
  's',
  true,
  3,
  1,
  [
    { ageMin: 18, ageMax: 39, male: 10, female: 10, origin: 'published' },
    { ageMin: 40, ageMax: 49, male: 10, female: 10, origin: 'published' },
    { ageMin: 50, ageMax: 59, male: 10, female: 10, origin: 'published' },
    { ageMin: 60, ageMax: 69, male: 10, female: 10, origin: 'published' },
    { ageMin: 70, ageMax: 79, male: 10, female: 10, origin: 'published' },
    { ageMin: 80, ageMax: 99, male: 8.7, female: 10, origin: 'published' },
  ]
);

/**
 * TUG, lower is better.
 * 20–49: Kear 2017 primary-care means (gender difference NS — same M/F).
 * 50–59: interpolated join (Kear 50s clinic mean 9.90 is slower than
 *        Bohannon healthy 60–69 8.1; see NORMS.md).
 * 60–99: Bohannon 2006 descriptive meta-analysis of healthy elders.
 */
export const TUG_NORMS: NormTable = byAge(
  'Bohannon RW. Reference values for the Timed Up and Go test: a descriptive meta-analysis. J Geriatr Phys Ther. 2006;29(2):64-68. Kear BM, Guck TP, McGaha AL. Timed Up and Go (TUG) test: normative reference values for ages 20 to 59 years. J Prim Care Community Health. 2017;8(1):9-13.',
  'tug',
  's',
  false,
  1.5,
  0.3,
  [
    { ageMin: 20, ageMax: 29, male: 8.57, female: 8.57, maleSd: 1.4, femaleSd: 1.4 },
    { ageMin: 30, ageMax: 39, male: 8.56, female: 8.56, maleSd: 1.23, femaleSd: 1.23 },
    { ageMin: 40, ageMax: 49, male: 8.86, female: 8.86, maleSd: 1.88, femaleSd: 1.88 },
    {
      ageMin: 50,
      ageMax: 59,
      male: 8.4,
      female: 8.4,
      maleSd: 1.5,
      femaleSd: 1.5,
      origin: 'interpolated',
    },
    { ageMin: 60, ageMax: 69, male: 8.1, female: 8.1 },
    { ageMin: 70, ageMax: 79, male: 9.2, female: 9.2 },
    { ageMin: 80, ageMax: 99, male: 11.3, female: 11.3 },
  ]
);

/** Bohannon 1997 comfortable gait speed (m/s). */
export const GAIT_NORMS: NormTable = byAge(
  'Bohannon RW. Comfortable and maximum walking speed of adults aged 20-79 years: reference values and determinants. Age Ageing. 1997;26:15-19.',
  'gait',
  'm/s',
  true,
  0.2,
  0.05,
  [
    { ageMin: 20, ageMax: 29, male: 1.39, female: 1.34 },
    { ageMin: 30, ageMax: 39, male: 1.46, female: 1.41 },
    { ageMin: 40, ageMax: 49, male: 1.46, female: 1.39 },
    { ageMin: 50, ageMax: 59, male: 1.39, female: 1.31 },
    { ageMin: 60, ageMax: 69, male: 1.36, female: 1.24 },
    { ageMin: 70, ageMax: 79, male: 1.26, female: 1.13 },
  ]
);

/**
 * Sitting-rising composite (0–10). Medians estimated from Araújo 2019
 * Figure 1 quartile chart (supplementary P50 table not retrieved) and
 * constrained by Brito/Araújo 2012 (score of 10 rare after 55; none >70
 * in that cohort). Cells are origin: estimated.
 */
export const SRT_NORMS: NormTable = byAge(
  'Araújo CGS, Castro CLT, Franca JF, Araújo DS. Sitting–rising test: sex- and age-reference scores derived from 6141 adults. Eur J Prev Cardiol. 2020;27(8):888-890. Brito LBB, Ricardo DR, Araújo DSMS, Ramos PS, Myers J, Araújo CGS. Ability to sit and rise from the floor as a predictor of all-cause mortality. Eur J Prev Cardiol. 2014;21(7):892-898 (2012 online).',
  'srt',
  'points',
  true,
  1.5,
  0.5,
  [
    { ageMin: 16, ageMax: 20, male: 10, female: 10, origin: 'estimated' },
    { ageMin: 21, ageMax: 25, male: 10, female: 10, origin: 'estimated' },
    { ageMin: 26, ageMax: 30, male: 9.5, female: 10, origin: 'estimated' },
    { ageMin: 31, ageMax: 35, male: 9, female: 10, origin: 'estimated' },
    { ageMin: 36, ageMax: 40, male: 8.5, female: 10, origin: 'estimated' },
    { ageMin: 41, ageMax: 45, male: 8, female: 9, origin: 'estimated' },
    { ageMin: 46, ageMax: 50, male: 7.5, female: 8.5, origin: 'estimated' },
    { ageMin: 51, ageMax: 55, male: 7, female: 7.5, origin: 'estimated' },
    { ageMin: 56, ageMax: 60, male: 6.5, female: 7, origin: 'estimated' },
    { ageMin: 61, ageMax: 65, male: 6, female: 6, origin: 'estimated' },
    { ageMin: 66, ageMax: 70, male: 5.5, female: 5.5, origin: 'estimated' },
    { ageMin: 71, ageMax: 75, male: 5, female: 4.5, origin: 'estimated' },
    { ageMin: 76, ageMax: 80, male: 4, female: 4, origin: 'estimated' },
    { ageMin: 81, ageMax: 85, male: 3.5, female: 3, origin: 'estimated' },
    { ageMin: 86, ageMax: 94, male: 3, female: 2.5, origin: 'estimated' },
  ]
);

export const L1_WEIGHTS = {
  cst: 0.25,
  sls: 0.25,
  tug: 0.2,
  gait: 0.15,
  srt: 0.15,
} as const;

export const L2_WEIGHTS = {
  sts: 0.25,
  srt: 0.3,
  eo: 0.15,
  ec: 0.15,
  tug: 0.1,
  gait: 0.05,
} as const;

export const MIN_WEIGHT = 0.6;
